import "./CommandPalette.scss";

import { Dialog, DialogContent, List } from "@mui/material";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { t } from "i18next";
import type { KeyboardEvent } from "react";
import type { TPortalType } from "store/types";

import { CommandPaletteHeader } from "./CommandPaletteHeader";
import { CompanyResultsList } from "./CompanyResultsList";
import { DataResultsList } from "./DataResultsList";
import { type SearchMode, useCommandPaletteState } from "./useCommandPaletteState";

type CommandPaletteProps = {
    open: boolean;
    onClose: () => void;
    navigateTo?: (path: string, state?: any) => void;
    portalType?: TPortalType;
};

export const CommandPalette = ({ open, onClose, navigateTo, portalType = "book" }: CommandPaletteProps) => {
    const {
        mode,
        setMode,
        query,
        setQuery,
        debouncedQuery,
        isSearching,
        searchResults,
        filteredCompanies,
        totalCompaniesCount,
        hasMultipleCompanies,
        isCrealoAdmin,
        companySettings,
        isLoadingCompanies,
        companyPaginatedElRef,
        handleSelectCompany,
        handleOpenTitle,
        handleOpenBeneficiary,
        handleCreateTitle,
        handleCreateBeneficiary,
        handleCreatePublication,
        handleCreateArticle,
        handleOpenPublication,
        handleOpenArticle,
        handleFilterTitleInManagement,
        handleFilterBeneficiaryInManagement,
        handleFilterArticleInManagement,
        toggleMode,
        isPressLike,
    } = useCommandPaletteState({ open, onClose, navigateTo, portalType });

    const canSwitchMode = isCrealoAdmin || hasMultipleCompanies;

    const modeOptions = [
        { value: "data" as SearchMode, label: t("CommandPalette.Data") },
        { value: "company" as SearchMode, label: t("CommandPalette.Companies") },
    ];

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== "Tab" || event.shiftKey || !canSwitchMode) return;
        event.preventDefault();
        toggleMode();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                className: "command-palette__dialog",
                sx: {
                    borderRadius: "6px",
                    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.20)",
                    height: "320px",
                    margin: "auto",
                    overflow: "hidden",
                    background: "rgba(255, 255, 255, 0.96)",
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.15)",
                    },
                },
            }}
        >
            <DialogContent className="command-palette__content" sx={{ p: 0 }} onKeyDown={handleKeyDown}>
                <div className="command-palette">
                    <CommandPaletteHeader
                        mode={mode}
                        setMode={setMode}
                        modeOptions={modeOptions}
                        isCrealoAdmin={isCrealoAdmin}
                        hasMultipleCompanies={hasMultipleCompanies}
                        isPressLike={isPressLike}
                        portalType={portalType}
                        query={query}
                        setQuery={setQuery}
                        t={t}
                    />

                    <ScrollShadowWrapper
                        className="command-palette__scroll-wrapper"
                        ref={mode === "company" && !query.trim() ? companyPaginatedElRef : undefined}
                    >
                        <List className="command-palette__list">
                            {mode === "company" && (
                                <CompanyResultsList
                                    companies={filteredCompanies}
                                    companySettings={companySettings}
                                    query={query}
                                    totalCount={totalCompaniesCount}
                                    isLoading={isLoadingCompanies}
                                    onSelectCompany={handleSelectCompany}
                                    t={t}
                                />
                            )}

                            {mode === "data" && (
                                <DataResultsList
                                    query={query}
                                    debouncedQuery={debouncedQuery}
                                    searchResults={searchResults}
                                    isSearching={isSearching}
                                    isPressLike={isPressLike}
                                    portalType={portalType}
                                    onCreateTitle={handleCreateTitle}
                                    onCreateBeneficiary={handleCreateBeneficiary}
                                    onCreatePublication={handleCreatePublication}
                                    onCreateArticle={handleCreateArticle}
                                    onOpenTitle={handleOpenTitle}
                                    onOpenPublication={handleOpenPublication}
                                    onOpenArticle={handleOpenArticle}
                                    onOpenBeneficiary={handleOpenBeneficiary}
                                    onFilterTitleInManagement={handleFilterTitleInManagement}
                                    onFilterArticleInManagement={handleFilterArticleInManagement}
                                    onFilterBeneficiaryInManagement={handleFilterBeneficiaryInManagement}
                                    t={t}
                                />
                            )}
                        </List>
                    </ScrollShadowWrapper>
                    {mode === "data" && (
                        <div className="command-palette__hint">
                            {t("CommandPalette.IdSearchHint") as string}
                            {canSwitchMode && <> • {t("CommandPalette.TabSwitchHint") as string}</>}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
